const API_URL = process.env.REACT_APP_API_URL;

export type EmailTemplateStorageUploadResponse = {
  success?: boolean;
  id?: string;
  _id?: string;
  data?: {
    _id?: string;
    id?: string;
    type?: string;
    name?: string;
    module_code?: string;
    is_system_hidden?: boolean;
  };
};

export const extractEmailTemplateStorageFileId = (
  raw: EmailTemplateStorageUploadResponse | Record<string, unknown> | null | undefined
): string => {
  if (!raw || typeof raw !== "object") return "";
  const anyRaw = raw as Record<string, unknown>;
  const data =
    anyRaw.data && typeof anyRaw.data === "object"
      ? (anyRaw.data as Record<string, unknown>)
      : null;

  const id =
    String(data?._id ?? "") ||
    String(data?.id ?? "") ||
    String(anyRaw._id ?? "") ||
    String(anyRaw.id ?? "");

  return String(id || "").trim();
};

export type CreateEmailTemplatePayload = {
  company_logo?: string;
  customer_message: string;
  default: boolean;
  social_links: {
    linkedin?: string;
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };
};

export type UpdateEmailTemplatePayload = {
  company_logo?: string;
  customer_message: string;
  /** PATCH body uses snake_case to match API / list rows (`is_default`). */
  is_default?: boolean;
  social_links: {
    linkedin?: string;
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };
};

export type EmailTemplatesDatatablePayload = {
  page: number;
  limit: number;
  search?: string;
  filters?: Record<string, unknown>;
  sort?: {
    field: string;
    order: "asc" | "desc";
  };
};

export type EmailTemplatesDatatableRow = {
  _id?: string;
  id?: string;
  company_logo?: string;
  company_logo_presigned_url?: string;
  customer_message?: string;
  message?: string;
  custom_message?: string;
  is_default?: boolean;
  default?: boolean;
  social_links?: {
    linkedin?: string;
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };
};

export type EmailTemplatesDatatableResponse = {
  success?: boolean;
  rows?: EmailTemplatesDatatableRow[];
  total?: number;
  data?: {
    rows?: EmailTemplatesDatatableRow[];
    total?: number;
  };
};

export type GetEmailTemplateByIdResponse = {
  success?: boolean;
  _id?: string;
  id?: string;
  company_logo?: string;
  company_logo_presigned_url?: string;
  customer_message?: string;
  message?: string;
  custom_message?: string;
  is_default?: boolean;
  default?: boolean;
  social_links?: {
    linkedin?: string;
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };
  data?: EmailTemplatesDatatableRow & {
    user_id?: string;
  };
};

export const uploadEmailTemplateStorageFile = async (
  token: string,
  file: File
): Promise<EmailTemplateStorageUploadResponse> => {
  const t = String(token || "").trim();
  if (!t) throw new Error("Missing auth token.");
  if (!file) throw new Error("Missing file.");

  const fd = new FormData();
  fd.append("file", file);

  const response = await fetch(`${API_URL}/email-templates-storage/files/upload`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${t}`,
    },
    body: fd,
  });

  const raw = await response.json().catch(() => ({}));
  if (!response.ok || (raw as any)?.error) {
    const message =
      (raw as any)?.error || (raw as any)?.message || "Failed to upload email template file";
    throw new Error(typeof message === "string" ? message : "Failed to upload email template file");
  }
  return raw as EmailTemplateStorageUploadResponse;
};

export const hardDeleteEmailTemplateStorageFile = async (
  token: string,
  fileId: string
): Promise<void> => {
  const t = String(token || "").trim();
  const id = String(fileId || "").trim();
  if (!t) throw new Error("Missing auth token.");
  if (!id) return;

  const response = await fetch(`${API_URL}/email-templates-storage/files/${encodeURIComponent(id)}/hard`, {
    method: "DELETE",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${t}`,
    },
  });

  if (response.ok) return;
  const raw = await response.json().catch(() => ({}));
  const message =
    (raw as any)?.error || (raw as any)?.message || "Failed to delete email template file";
  throw new Error(typeof message === "string" ? message : "Failed to delete email template file");
};

export const createEmailTemplate = async (
  token: string,
  payload: CreateEmailTemplatePayload
): Promise<unknown> => {
  const t = String(token || "").trim();
  if (!t) throw new Error("Missing auth token.");

  const response = await fetch(`${API_URL}/email-templates`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${t}`,
    },
    body: JSON.stringify(payload),
  });

  const raw = await response.json().catch(() => ({}));
  if (!response.ok || (raw as any)?.error) {
    const message =
      (raw as any)?.error || (raw as any)?.message || "Failed to create email template";
    throw new Error(typeof message === "string" ? message : "Failed to create email template");
  }

  return raw;
};

export const fetchEmailTemplatesDatatable = async (
  token: string,
  payload: EmailTemplatesDatatablePayload
): Promise<{ rows: EmailTemplatesDatatableRow[]; total: number }> => {
  const t = String(token || "").trim();
  if (!t) throw new Error("Missing auth token.");

  const response = await fetch(`${API_URL}/email-templates/datatable`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${t}`,
    },
    body: JSON.stringify({
      page: payload.page,
      limit: payload.limit,
      search: payload.search ?? "",
      filters: payload.filters ?? {},
      sort: payload.sort ?? { field: "created_at", order: "desc" },
    }),
  });

  const raw: EmailTemplatesDatatableResponse = await response.json().catch(() => ({}));
  if (!response.ok || (raw as any)?.error) {
    const message =
      (raw as any)?.error || (raw as any)?.message || "Failed to fetch email templates";
    throw new Error(typeof message === "string" ? message : "Failed to fetch email templates");
  }

  const rows = Array.isArray(raw?.data?.rows)
    ? raw.data!.rows!
    : Array.isArray(raw?.rows)
      ? raw.rows
      : [];
  const total = Number(raw?.data?.total ?? raw?.total ?? rows.length) || rows.length;
  return { rows, total };
};

export const fetchEmailTemplateById = async (
  token: string,
  templateId: string
): Promise<EmailTemplatesDatatableRow> => {
  const t = String(token || "").trim();
  const id = String(templateId || "").trim();
  if (!t) throw new Error("Missing auth token.");
  if (!id) throw new Error("Missing email template id.");

  const response = await fetch(`${API_URL}/email-templates/${encodeURIComponent(id)}`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${t}`,
    },
  });
  const raw: GetEmailTemplateByIdResponse = await response.json().catch(() => ({}));
  if (!response.ok || (raw as any)?.error) {
    const message =
      (raw as any)?.error || (raw as any)?.message || "Failed to fetch email template";
    throw new Error(typeof message === "string" ? message : "Failed to fetch email template");
  }
  const row = (raw?.data && typeof raw.data === "object" ? raw.data : raw) as EmailTemplatesDatatableRow;
  return row ?? {};
};

export const updateEmailTemplate = async (
  token: string,
  templateId: string,
  payload: UpdateEmailTemplatePayload
): Promise<unknown> => {
  const t = String(token || "").trim();
  const id = String(templateId || "").trim();
  if (!t) throw new Error("Missing auth token.");
  if (!id) throw new Error("Missing email template id.");

  const response = await fetch(`${API_URL}/email-templates/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${t}`,
    },
    body: JSON.stringify(payload),
  });
  const raw = await response.json().catch(() => ({}));
  if (!response.ok || (raw as any)?.error) {
    const message =
      (raw as any)?.error || (raw as any)?.message || "Failed to update email template";
    throw new Error(typeof message === "string" ? message : "Failed to update email template");
  }
  return raw;
};

export const deleteEmailTemplate = async (
  token: string,
  templateId: string
): Promise<{ deleted: boolean }> => {
  const t = String(token || "").trim();
  const id = String(templateId || "").trim();
  if (!t) throw new Error("Missing auth token.");
  if (!id) throw new Error("Missing email template id.");

  const response = await fetch(`${API_URL}/email-templates/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${t}`,
    },
  });
  const raw = await response.json().catch(() => ({}));
  if (!response.ok || (raw as any)?.error) {
    const message =
      (raw as any)?.error || (raw as any)?.message || "Failed to delete email template";
    throw new Error(typeof message === "string" ? message : "Failed to delete email template");
  }
  return { deleted: Boolean((raw as any)?.deleted) };
};

/**
 * Public: GET /api/v1/public/email-templates/users/:userId — no auth.
 * Used for transactional email header/footer (logo, socials, customer message).
 */
export const fetchPublicEmailTemplateByUserId = async (
  userId: string
): Promise<EmailTemplatesDatatableRow> => {
  const base = String(API_URL || "").replace(/\/+$/, "");
  const id = String(userId || "").trim();
  if (!base) throw new Error("Missing REACT_APP_API_URL.");
  if (!id) throw new Error("Missing user id for public email template.");

  const response = await fetch(
    `${base}/public/email-templates/users/${encodeURIComponent(id)}`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
    }
  );

  const raw = await response.json().catch(() => ({}));
  if (!response.ok || (raw as any)?.error) {
    const message =
      (raw as any)?.error || (raw as any)?.message || "Failed to fetch public email template";
    throw new Error(typeof message === "string" ? message : "Failed to fetch public email template");
  }

  const row = (raw?.data && typeof raw.data === "object" ? raw.data : raw) as EmailTemplatesDatatableRow;
  return row && typeof row === "object" ? row : {};
};

/** Public setting key: global BCC-style recipients for order/job payment notifications. */
export const ORDER_JOB_NOTIFICATION_EMAILS_SETTING_KEY = "order_job.notification_emails";

export type PublicGeneralSettingRow = {
  _id?: string;
  description?: string;
  enabled?: boolean;
  key?: string;
  emails?: string[];
  string_array_value?: string[];
  exists?: boolean;
  owner_node_id?: string;
  owner_node_type?: string;
  value_type?: string;
  updated_at?: string;
};

type PublicGeneralSettingByKeyResponse = {
  success?: boolean;
  _id?: string;
  setting?: PublicGeneralSettingRow;
  data?: { setting?: PublicGeneralSettingRow };
  error?: string;
};

/**
 * Public: GET …/public/settings/general/bykey?owner_node_id=…&key=… — no auth.
 * Returns the `setting` object or null when missing / error.
 */
export const fetchPublicGeneralSettingByKey = async (
  key: string,
  ownerNodeId: string
): Promise<PublicGeneralSettingRow | null> => {
  const base = String(API_URL || "").replace(/\/+$/, "");
  const k = String(key || "").trim();
  const owner = String(ownerNodeId || "").trim();
  if (!base || !k || !owner) return null;

  const url = `${base}/public/settings/general/bykey?owner_node_id=${encodeURIComponent(owner)}&key=${encodeURIComponent(k)}`;
  const response = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  const raw = (await response.json().catch(() => ({}))) as PublicGeneralSettingByKeyResponse;
  if (!response.ok || raw?.error) {
    console.warn("[fetchPublicGeneralSettingByKey] HTTP", response.status, raw);
    return null;
  }
  const setting =
    raw.setting && typeof raw.setting === "object"
      ? raw.setting
      : raw.data?.setting && typeof raw.data.setting === "object"
        ? raw.data.setting
        : raw && typeof raw === "object" && ((raw as PublicGeneralSettingByKeyResponse)._id || (raw as any).emails)
          ? (raw as PublicGeneralSettingRow)
        : null;
  return setting;
};

/** Lowercased, trimmed emails from `string_array_value` when the setting is enabled (or `enabled` omitted). */
export function parsePublicSettingNotificationEmails(
  setting: PublicGeneralSettingRow | null | undefined
): string[] {
  if (!setting || typeof setting !== "object") return [];
  if (setting.enabled === false) return [];
  const arr = Array.isArray(setting.emails)
    ? setting.emails
    : Array.isArray(setting.string_array_value)
      ? setting.string_array_value
      : [];
  if (!Array.isArray(arr)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  arr.forEach((e) => {
    const email = String(e || "").trim().toLowerCase();
    if (!email || seen.has(email)) return;
    seen.add(email);
    out.push(email);
  });
  return out;
};

/** Convenience: emails for {@link ORDER_JOB_NOTIFICATION_EMAILS_SETTING_KEY}. */
export async function fetchPublicOrderJobNotificationEmails(ownerNodeId: string): Promise<string[]> {
  const row = await fetchPublicGeneralSettingByKey(
    ORDER_JOB_NOTIFICATION_EMAILS_SETTING_KEY,
    ownerNodeId
  );
  return parsePublicSettingNotificationEmails(row);
}
